import type { ReactNode } from "react";
import type { RadioConfigEntry } from "../api/types";
import { colorFor } from '../lib/definitions/palette';
import { configEnumLabel, configFieldLabel, meshcoreAdvTypeLabel, meshtasticSectionLabel } from '../lib/definitions/settings';
import { isProtocol, PROTOCOL_COLOR, PROTOCOL_LABEL } from '../lib/definitions/protocols';
import { relativeTime } from "../lib/format";
import { MESHTASTIC_CHANNEL_FIELDS, MESHTASTIC_CONFIG_SECTIONS, MESHTASTIC_MODULE_SECTIONS } from "../lib/meshtasticSchema.generated";
import type { MeshtasticField } from "../lib/meshtasticSchema.generated";
import { camelToSnake, formatLatLon, formatScalar, isRedacted, positionSourceLabel, resolveMeshtasticKey } from "../lib/radioSnapshot";
import { useAppData } from "../state/AppData";

// Presentation half of Settings > Node Configuration > Live snapshot
// (this file's own RadioTransportPanel) — replaces the raw
// PayloadView key-value dump with grouped, labeled, aligned rows, via this
// file's own .radio-snapshot-grid/.radio-snapshot-row (global.css) two-
// column pattern. (Polish pass, 2026-09-22: originally reused
// NodeDetailPage.tsx's .node-detail-grid/.node-detail-row, but that pair's
// narrower columns and value-shrinks-not-label sizing broke at wide
// viewports on this page's longer Meshtastic config labels — see
// global.css's own comment on .radio-snapshot-grid for the verified
// breakage. NodeDetailPage.tsx itself is untouched.) Label/enum/range
// resolution itself lives in lib/definitions.ts and lib/radioSnapshot.ts —
// this file only turns resolved rows into markup.
//
// HONESTY MARKERS this file is responsible for (see each component's own
// comment for where): the position "real gap" note (PositionSnapshot), the
// channels "real gap" note (RadioTransportPanel, below), which gates
// whether this file's ChannelsSnapshot renders at all, the
// "<redacted>" marker (RedactionChip, never a real or fake value), and the
// new default-omission note (MeshtasticDeviceSnapshot) explaining that a
// field the device doesn't report is at its built-in default, not unknown.

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

interface Row {
  key: string;
  label: string;
  value: ReactNode;
  mono?: boolean;
  // Full-panel-width modifier (task brief item 2) — deliberately separate
  // from `mono`: every key-material row is mono, but not every mono row is
  // key material (position lat/lon pairs and the short Meshtastic node_id
  // are mono for the fixed-width look but would look silly full-width).
  // Set only where a value is genuinely long key material — see each call
  // site's own comment.
  wide?: boolean;
}

function DefinitionGrid({ rows }: { rows: Row[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="radio-snapshot-grid">
      {rows.map((r) => (
        <div className={r.wide ? "radio-snapshot-row radio-snapshot-row--wide" : "radio-snapshot-row"} key={r.key}>
          <span className="k">{r.label}</span>
          <span className={r.mono ? "v radio-mono-value" : "v"}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

// The one place "<redacted>" is ever rendered — a real, present value
// (the field exists and is set) that the backend deliberately never sends,
// never a real or fabricated key (task brief's own hard constraint).
function RedactionChip() {
  return <span className="radio-redaction-chip">{"<redacted>"}</span>;
}

function Subsection({ heading, rows }: { heading: string; rows: Row[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="radio-snapshot-subsection">
      <h5>{heading}</h5>
      <DefinitionGrid rows={rows} />
    </div>
  );
}

// A value whose field name suggests key material (public keys) — the ONE
// deliberate monospace-face exception noted in the task brief: these read
// better as fixed-width hex/base64 than as prose text. Never applied to a
// redacted field (RedactionChip already handles those). Also given the
// full-panel-width treatment (.radio-snapshot-row--wide, polish pass
// 2026-09-22) since a real key never fits, and reads badly cramped, inside
// one ~300px grid column — currently this only matches
// device.config.security.publicKey (security.privateKey/adminKey are
// either redacted or non-string and never reach this check).
function looksLikeKeyMaterial(jsonKey: string, value: unknown): boolean {
  return typeof value === "string" && value.length > 12 && /key/i.test(jsonKey);
}

// tzdef (device.config.device.tzdef) is a POSIX timezone string, e.g.
// "MST7MDT,M3.2.0,M11.1.0" — no spaces, so it can't wrap at word
// boundaries like prose, but unlike a key it reads fine inside a normal
// column, so it gets the mono class (for the overflow-wrap: anywhere
// exception, polish pass 2026-09-22) without the wide modifier.
function isUnbreakableToken(jsonKey: string): boolean {
  return jsonKey === "tzdef";
}

// Builds rows for one Meshtastic config/module leaf object (e.g.
// device.config.lora, or a channel's settings), flattening any nested plain
// object ONE level (e.g. a channel's settings.moduleSettings) rather than
// nesting indentation — the task brief's explicit "do not nest indentation"
// rule. `fields` is the matching meshtasticSchema.generated.ts field list
// for this exact section when known (undefined for a flattened nested
// object, which has no field list of its own — its keys resolve through the
// camelCase->snake_case fallback in resolveMeshtasticKey).
function buildRows(obj: Record<string, unknown>, fields: readonly MeshtasticField[] | undefined, keyPrefix = ""): Row[] {
  const rows: Row[] = [];
  for (const [jsonKey, value] of Object.entries(obj)) {
    if (value === null || value === undefined) continue;
    const rowKey = keyPrefix + jsonKey;
    if (isRedacted(value)) {
      const resolved = resolveMeshtasticKey(fields, jsonKey);
      rows.push({ key: rowKey, label: resolved.label, value: <RedactionChip /> });
      continue;
    }
    if (isPlainObject(value)) {
      rows.push(...buildRows(value, undefined, `${rowKey}.`));
      continue;
    }
    const resolved = resolveMeshtasticKey(fields, jsonKey);
    const displayValue = resolved.isEnum ? configEnumLabel(String(value)) : formatScalar(value);
    const keyMaterial = looksLikeKeyMaterial(jsonKey, value);
    rows.push({
      key: rowKey,
      label: resolved.label,
      value: displayValue,
      mono: keyMaterial || isUnbreakableToken(jsonKey),
      wide: keyMaterial,
    });
  }
  return rows;
}

// --- Identity ---------------------------------------------------------

export function IdentitySnapshot({ protocol, identity }: { protocol: string; identity: Record<string, unknown> }) {
  const rows: Row[] = [];
  for (const [key, value] of Object.entries(identity)) {
    if (value === null || value === undefined) continue;
    const label = configFieldLabel(key); // backend-authored, already snake_case
    let displayValue: ReactNode;
    let mono = false;
    let wide = false;
    if (protocol === "meshcore" && key === "adv_type" && typeof value === "number") {
      // See MESHCORE_ADV_TYPE_LABEL (lib/definitions.ts) for the two
      // independent sources backing this mapping.
      displayValue = meshcoreAdvTypeLabel(value);
    } else if (protocol === "meshtastic" && (key === "role" || key === "hw_model")) {
      // Confirmed real protobuf enums (Config.DeviceConfig.Role /
      // HardwareModel respectively — meshtastic==2.7.11 introspection, see
      // this task's own report) even though neither field lives in
      // meshtasticSchema.generated.ts (that generator only walks
      // Config/ModuleConfig/Channel, not NodeInfo/DeviceMetadata).
      displayValue = configEnumLabel(String(value));
    } else if (key === "public_key" || key === "node_id") {
      mono = true;
      // Full-panel-width only for an actual key (MeshCore identity's own
      // 64-char hex public key) — NOT for node_id, which stays mono for the
      // fixed-width look but is short (Meshtastic's "!xxxxxxxx") and would
      // look silly stretched across the whole row (task brief item 2's own
      // explicit warning).
      wide = key === "public_key";
      displayValue = String(value);
    } else {
      displayValue = formatScalar(value);
    }
    rows.push({ key, label, value: displayValue, mono, wide });
  }
  return <DefinitionGrid rows={rows} />;
}

// --- Position -----------------------------------------------------------
// CORRECTNESS FIX (task brief): the old code read `entry.position`, a key
// the live backend never sends, so the "real gap" note below rendered on
// EVERY transport regardless of whether a position was actually known.
// Reads the real fields (configured/broadcast/effective/source) instead.

export function PositionSnapshot({ entry }: { entry: RadioConfigEntry }) {
  const configured = entry.configured_position ?? null;
  const broadcast = entry.broadcast_position ?? null;
  const effective = entry.effective_position ?? null;
  const source = entry.position_source ?? "none";

  if (!configured && !broadcast && !effective) {
    // Verbatim, word-for-word, per the task brief — this is the case it
    // still describes correctly (e.g. the MeshCore vnode, which has no
    // position concept at all).
    return <div className="node-detail-note">No position available from this source — a real gap, not an empty table.</div>;
  }

  const rows: Row[] = [];
  const effStr = formatLatLon(effective);
  if (effStr) rows.push({ key: "effective", label: "Effective position", value: effStr, mono: true });
  rows.push({ key: "source", label: "Position source", value: positionSourceLabel(source) });
  const confStr = formatLatLon(configured);
  if (confStr) rows.push({ key: "configured", label: "Configured position", value: confStr, mono: true });
  const bcStr = formatLatLon(broadcast);
  if (bcStr) rows.push({ key: "broadcast", label: "Broadcast position", value: bcStr, mono: true });
  return <DefinitionGrid rows={rows} />;
}

// --- Device: MeshCore (flat) ----------------------------------------------

function MeshCoreDeviceSnapshot({ device }: { device: Record<string, unknown> }) {
  const rows: Row[] = Object.entries(device)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([key, value]) => ({ key, label: configFieldLabel(key), value: formatScalar(value) }));
  return <DefinitionGrid rows={rows} />;
}

// --- Device: Meshtastic (nested config/module_config/metadata) -----------

// Device metadata (protobuf DeviceMetadata) isn't covered by
// meshtasticSchema.generated.ts (that generator only walks
// Config/ModuleConfig/Channel) — camelCase keys go through
// camelToSnake+configFieldLabel same as any other unmatched key, with
// `role`/`hwModel` explicitly enum-glossed (see IdentitySnapshot's own
// comment on the same two fields for why that's safe here).
function buildMetadataRows(metadata: Record<string, unknown>): Row[] {
  const rows: Row[] = [];
  for (const [camelKey, value] of Object.entries(metadata)) {
    if (value === null || value === undefined) continue;
    const label = configFieldLabel(camelToSnake(camelKey));
    const displayValue = camelKey === "role" || camelKey === "hwModel" ? configEnumLabel(String(value)) : formatScalar(value);
    rows.push({ key: camelKey, label, value: displayValue });
  }
  return rows;
}

// New, small, honest note (task brief item 6) — protobuf's MessageToDict
// (which produces this live snapshot) omits any field currently at its
// type's zero/default value; a module section reporting `{}` is reporting
// "nothing here differs from the built-in default," not "unknown" or
// "disabled." Kept in one place rather than repeated per subsection.
const DEFAULT_OMISSION_NOTE =
  "This device reports only the settings that differ from their built-in default. Anything not shown here is set to that default, not unknown.";

function MeshtasticDeviceSnapshot({ device }: { device: Record<string, unknown> }) {
  const config = isPlainObject(device.config) ? device.config : {};
  const moduleConfig = isPlainObject(device.module_config) ? device.module_config : {};
  const metadata = isPlainObject(device.metadata) ? device.metadata : {};

  const configSections = Object.entries(config)
    .filter(([, v]) => isPlainObject(v))
    .map(([sectionKey, value]) => (
      <Subsection key={sectionKey} heading={meshtasticSectionLabel(sectionKey)} rows={buildRows(value as Record<string, unknown>, MESHTASTIC_CONFIG_SECTIONS[sectionKey])} />
    ));

  const moduleSections: ReactNode[] = [];
  // Module sections reporting {} are at their default — collapsed into one
  // honest muted line rather than an empty box per section (task brief item
  // 4: "do not claim they are disabled").
  const emptyModuleLabels: string[] = [];
  for (const [camelKey, value] of Object.entries(moduleConfig)) {
    if (!isPlainObject(value)) continue;
    const snakeKey = camelToSnake(camelKey);
    const heading = meshtasticSectionLabel(snakeKey);
    if (Object.keys(value).length === 0) {
      emptyModuleLabels.push(heading);
      continue;
    }
    moduleSections.push(<Subsection key={camelKey} heading={heading} rows={buildRows(value, MESHTASTIC_MODULE_SECTIONS[snakeKey])} />);
  }

  const metadataRows = buildMetadataRows(metadata);

  return (
    <>
      <p className="radio-snapshot-default-note">{DEFAULT_OMISSION_NOTE}</p>
      {configSections}
      {moduleSections}
      <Subsection heading={meshtasticSectionLabel("metadata")} rows={metadataRows} />
      {emptyModuleLabels.length > 0 && (
        <p className="radio-snapshot-collapsed-defaults">Reporting no non-default settings: {emptyModuleLabels.join(", ")}.</p>
      )}
    </>
  );
}

export function DeviceSnapshot({ entry }: { entry: RadioConfigEntry }) {
  const device = entry.device ?? {};
  if (entry.protocol === "meshcore") return <MeshCoreDeviceSnapshot device={device} />;
  return <MeshtasticDeviceSnapshot device={device} />;
}

// --- Channels --------------------------------------------------------------
// One card per channel, flat rows, no nested indentation (task brief item
// 5) — the worst offender in the old raw PayloadView dump.

export function ChannelsSnapshot({ channels }: { channels: Record<string, unknown>[] }) {
  return (
    <div className="radio-channel-grid">
      {channels.map((ch, i) => {
        const index = typeof ch.index === "number" ? ch.index : 0; // a missing `index` key means index 0, not unknown
        const settings = isPlainObject(ch.settings) ? ch.settings : {};
        const name = typeof settings.name === "string" ? settings.name : null;
        const roleValue = ch.role;
        const uplink = settings.uplinkEnabled === true;
        const downlink = settings.downlinkEnabled === true;

        const rows: Row[] = [
          { key: "name", label: configFieldLabel("name"), value: name ?? "—" },
          {
            key: "role",
            label: configFieldLabel("role"),
            value: typeof roleValue === "string" ? configEnumLabel(roleValue) : formatScalar(roleValue),
          },
          { key: "uplink", label: configFieldLabel("uplink_enabled"), value: uplink ? "On" : "Off" },
        ];
        if ("downlinkEnabled" in settings) {
          rows.push({ key: "downlink", label: configFieldLabel("downlink_enabled"), value: downlink ? "On" : "Off" });
        }

        // Any other top-level channel keys besides index/settings/role
        // (e.g. `id`) — resolved via MESHTASTIC_CHANNEL_FIELDS.
        for (const [k, v] of Object.entries(ch)) {
          if (k === "index" || k === "settings" || k === "role" || v === null || v === undefined) continue;
          const resolved = resolveMeshtasticKey(MESHTASTIC_CHANNEL_FIELDS, k);
          rows.push({ key: `ch-${k}`, label: resolved.label, value: resolved.isEnum ? configEnumLabel(String(v)) : formatScalar(v) });
        }

        // Any other settings keys besides name/uplinkEnabled/downlinkEnabled/psk
        // — includes moduleSettings, flattened one level (no nested indentation).
        for (const [k, v] of Object.entries(settings)) {
          if (k === "name" || k === "uplinkEnabled" || k === "downlinkEnabled" || k === "psk" || v === null || v === undefined) continue;
          if (isRedacted(v)) {
            const resolved = resolveMeshtasticKey(MESHTASTIC_CHANNEL_FIELDS, k);
            rows.push({ key: `set-${k}`, label: resolved.label, value: <RedactionChip /> });
            continue;
          }
          if (isPlainObject(v)) {
            for (const [nestedKey, nestedValue] of Object.entries(v)) {
              if (nestedValue === null || nestedValue === undefined) continue;
              const resolved = resolveMeshtasticKey(undefined, nestedKey);
              rows.push({ key: `ms-${nestedKey}`, label: resolved.label, value: formatScalar(nestedValue) });
            }
            continue;
          }
          const resolved = resolveMeshtasticKey(MESHTASTIC_CHANNEL_FIELDS, k);
          rows.push({ key: `set-${k}`, label: resolved.label, value: resolved.isEnum ? configEnumLabel(String(v)) : formatScalar(v) });
        }

        // PSK shown last, always the literal marker, never a real or fake
        // key — but only when the payload actually reported a `psk` key for
        // this channel. Every real channel currently has one, but asserting
        // the row unconditionally would claim a redacted PSK exists even for
        // a channel whose payload never contained the key at all.
        if ("psk" in settings) {
          rows.push({ key: "psk", label: configFieldLabel("psk"), value: <RedactionChip /> });
        }

        return (
          <div className="radio-channel-card" key={i}>
            <div className="radio-channel-card-title">Channel {index}</div>
            <DefinitionGrid rows={rows} />
          </div>
        );
      })}
    </div>
  );
}

// The component below this comment (RadioTransportPanel) is the live
// snapshot this section always had — Identity, Position, Device, Channels
// per transport, via GET /api/radio/config. It's still useful as a "what
// does the radio currently report" view, it's just no longer the only thing
// this section offers, and no longer framed as read-only-by-design. Not a
// new nav destination, per instruction.
//
// Rendering moved 2026-09-23: with Radio control now one tab per transport
// (RadioControlPanel.tsx), a single "Live snapshot" block covering every
// transport at once no longer matched the page — it now renders per
// transport, inside that transport's own tab (RadioControlPanel.tsx's
// TransportPanel), filtered to that transport's entry only. This component
// moved into this file (exported) alongside the other snapshot presentation
// pieces this file's header comment already documents, since that's what it
// actually is and it removed a circular import between RadioConfigSection.tsx
// and RadioControlPanel.tsx.
//
// Content is genuinely node configuration, not a diagnostic dump: Identity,
// Position, Device, and Channels, per transport. Presentation rebuilt
// 2026-09-22: the raw PayloadView key-value dump read as a diagnostic dump
// and looked nothing like the typed forms below it, so it was replaced with
// grouped, labeled, aligned rows via
// components/RadioSnapshotView.tsx (identity/position/device/channels
// presentation + label/enum resolution) rather than the generic recursive
// PayloadView renderer, which stays in use elsewhere (Packets page) where a
// raw protobuf field dump is exactly what that audience wants. Same fix
// also corrects a real bug: the old code read `entry.position`, a key the
// live backend never actually sends, so the "no position" gap note used to
// render even for the Meshtastic transport that has a real, reported
// position — see PositionSnapshot's own comment.
export function RadioTransportPanel({ entry }: { entry: RadioConfigEntry }) {
  const { effectiveTheme } = useAppData();
  const protocol = entry.protocol;
  const protocolInfo = isProtocol(protocol) ? { label: PROTOCOL_LABEL[protocol], color: PROTOCOL_COLOR[protocol] } : null;

  const channels = Array.isArray(entry.channels) ? entry.channels : [];
  const channelsEmpty = channels.length === 0;

  return (
    <div className="radio-transport-panel">
      <div className="radio-transport-header">
        <h4>{entry.transport_id}</h4>
        {protocolInfo && (
          <span className="badge" style={{ background: colorFor(protocolInfo.color, effectiveTheme) }}>
            {protocolInfo.label}
          </span>
        )}
        {entry.captured_at && <span className="settings-field-desc">Captured {relativeTime(entry.captured_at)}</span>}
      </div>

      {/* Prominent, not a tooltip — presenting this as confirmed
          physical-radio fact would be precisely the false-confidence
          failure this platform exists to catch. */}
      {!entry.reflects_physical_radio && (
        <div className="radio-caveat-banner">
          <strong>Not confirmed to reflect the physical radio.</strong>
          <p>{entry.caveat || "This source has not confirmed its data matches the physical device."}</p>
        </div>
      )}

      <div className="node-detail-section">
        <h3>Identity</h3>
        <IdentitySnapshot protocol={protocol} identity={entry.identity ?? {}} />
      </div>

      <div className="node-detail-section">
        <h3>Position</h3>
        <PositionSnapshot entry={entry} />
      </div>

      <div className="node-detail-section">
        <h3>Device</h3>
        <DeviceSnapshot entry={entry} />
      </div>

      <div className="node-detail-section">
        <h3>Channels</h3>
        {channelsEmpty ? (
          <div className="node-detail-note">No channels available from this source — a real gap, not "no channels configured."</div>
        ) : (
          <ChannelsSnapshot channels={channels} />
        )}
      </div>
    </div>
  );
}
